import { memo, useMemo } from 'react';

import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_SHORT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IKnmPreview } from 'APIPackage/KnmSearchModule/interfaces/IKnmPreview';
import { formatDateLocale } from 'CorePackage/MainModule/common/formatDate';

import styles from './KnmPreview.module.scss';
import { KnmSubtitle } from './KnmSubtitle';
import { convertArrayToDate } from '../common/convertArrayToDate';
import { parseErknmValue } from '../common/parseErknmValue';
import { KnmSubjectTypesEnum } from '../../../KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';

interface IErknmFaceProps {
  passportData: IKnmPreview;
}

export const ErknmFace = memo(({ passportData }: IErknmFaceProps) => {
  const isMspCode =
    passportData.inspectionErknm?.organizations &&
    (passportData.inspectionErknm.organizations[0].isIndividualEntrepreneur ||
      passportData.inspectionErknm.organizations[0].isLegalEntity ||
      passportData.inspectionErknm.organizations[0].isSmallEnterprise);

  const isSonko = passportData.inspectionErknm?.organizations && passportData.inspectionErknm.organizations[0].isSonko;

  const subjectType = passportData?.inspectionErknm?.organizations?.[0].subjectType;
  const country = passportData?.inspectionErknm?.organizations?.[0].country;
  const snils = passportData?.inspectionErknm?.organizations?.[0].snils;
  const document = passportData?.inspectionErknm?.organizations?.[0].document;
  const locationAddress = passportData?.inspectionErknm?.organizations?.[0].locationAddress;
  const workAddress = passportData?.inspectionErknm?.organizations?.[0].workAddress;
  const rafpNza = passportData?.inspectionErknm?.organizations?.[0].rafpNza;
  const factualAddress = passportData?.inspectionErknm?.organizations?.[0].factualAddress;
  const nkoNumber = passportData?.inspectionErknm?.organizations?.[0].nkoForeignAgentsRegistryNumber;
  const nkoInclusionDate = passportData?.inspectionErknm?.organizations?.[0].nkoForeignAgentsRegistryInclusionDate;
  const nkoExclusionDate = passportData?.inspectionErknm?.organizations?.[0].nkoForeignAgentsRegistryExclusionDate;
  const kpp = passportData?.inspectionErknm?.organizations?.[0].kpp;

  const organizationNameColText = useMemo(() => {
    switch (subjectType) {
      case KnmSubjectTypesEnum.FIZ:
      case KnmSubjectTypesEnum.IFIZ:
        return 'Фамилия, имя, отчество (при наличии)';
      case KnmSubjectTypesEnum.IUR:
        return 'Наименование';
      case KnmSubjectTypesEnum.FIUR:
        return 'Наименование организации';
      default:
        return 'Наименование проверяемого лица';
    }
  }, [subjectType]);

  const countryColText = useMemo(() => {
    if (subjectType === KnmSubjectTypesEnum.IFIZ) {
      return 'Гражданство (при наличии)';
    }

    return 'Государство, в котором указанное юридическое лицо зарегистрировано';
  }, [subjectType]);

  return (
    <div className={styles.ColumnContent}>
      <span className={styles.SectionTitle}>Сведения о контролируемом лице</span>
      {subjectType && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Тип контролируемого лица</div>
          <div className={styles.ColValue}>{subjectType}</div>
        </div>
      )}
      {passportData.inspectionErknm?.organizations && passportData.inspectionErknm.organizations[0].inn && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Идентификационный номер налогоплательщика (ИНН)</div>
          <div className={styles.ColValue}>{passportData.inspectionErknm.organizations[0].inn}</div>
        </div>
      )}
      {passportData.inspectionErknm?.organizations && passportData.inspectionErknm.organizations[0].ogrn && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Основной государственный регистрационный номер (ОГРН)</div>
          <div className={styles.ColValue}>{passportData.inspectionErknm.organizations[0].ogrn}</div>
        </div>
      )}
      {snils && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Страховой номер индивидуального лицевого счета (СНИЛС)</div>
          <div className={styles.ColValue}>{snils}</div>
        </div>
      )}
      {rafpNza && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Номер записи об аккредитации (НЗА)</div>
          <div className={styles.ColValue}>{rafpNza}</div>
        </div>
      )}
      {kpp && (
        <div className={styles.Row}>
          <div className={styles.ColText}>КПП</div>
          <div className={styles.ColValue}>{kpp}</div>
        </div>
      )}
      {passportData.inspectionErknm?.organizations && passportData.inspectionErknm.organizations[0].organizationName && (
        <div className={styles.Row}>
          <div className={styles.ColText}>{organizationNameColText}</div>
          <div className={styles.ColValue}>{passportData.inspectionErknm.organizations[0].organizationName}</div>
        </div>
      )}
      {country && (
        <div className={styles.Row}>
          <div className={styles.ColText}>{countryColText}</div>
          <div className={styles.ColValue}>{country}</div>
        </div>
      )}
      {isMspCode && (
        <div className={styles.Row}>
          <div className={styles.ColText}>
            Сведения об отнесении контролируемого лица к субъектам малого и среднего бизнеса
          </div>
          <div className={styles.ColValue}>{isMspCode}</div>
        </div>
      )}
      {typeof isSonko === 'boolean' && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Наличие проверяемого лица в реестре СОНКО</div>
          <div className={styles.ColValue}>{isSonko ? 'Да' : 'Нет'}</div>
        </div>
      )}
      {document && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Сведения о документе, удостоверяющем личность</div>
          <div className={styles.ColValue}>{document}</div>
        </div>
      )}
      {locationAddress && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Адрес места нахождения</div>
          <div className={styles.ColValue}>{locationAddress}</div>
        </div>
      )}
      {workAddress && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Место нахождения (осуществления деятельности) контролируемого лица</div>
          <div className={styles.ColValue}>{workAddress}</div>
        </div>
      )}
      {factualAddress && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Место фактического осуществления деятельности</div>
          <div className={styles.ColValue}>{factualAddress}</div>
        </div>
      )}
      {subjectType === KnmSubjectTypesEnum.UR && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Наличие в реестре НКО, выполняющих функции иностранного агента</div>
          <div className={styles.ColValue}>{nkoNumber ? 'Да' : 'Нет'}</div>
        </div>
      )}
      {nkoNumber && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Регистрационный номер</div>
          <div className={styles.ColValue}>{nkoNumber}</div>
        </div>
      )}
      {nkoInclusionDate && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Дата включения</div>
          <div className={styles.ColValue}>{nkoInclusionDate}</div>
        </div>
      )}
      {nkoExclusionDate && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Дата исключения</div>
          <div className={styles.ColValue}>{nkoExclusionDate}</div>
        </div>
      )}
      {passportData.inspectionErknm?.organizations && passportData.inspectionErknm.organizations[0].okveds && (
        <>
          <span className={styles.SectionTitle}>ОКВЭД</span>
          {passportData.inspectionErknm.organizations[0].okveds.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>ОКВЭД №{index + 1}</span>
              {item.name && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Наименование</div>
                  <div className={styles.ColValue}>{item.name}</div>
                </div>
              )}
            </div>
          ))}
        </>
      )}
      {passportData.inspectionErknm?.objects && passportData.inspectionErknm.objects.length > 0 && (
        <KnmSubtitle title="Сведения об объектах контроля">
          {passportData.inspectionErknm.objects.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>Объект №{index + 1}</span>
              {item.address && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Местонахождение</div>
                  <div className={styles.ColValue}>{item.address}</div>
                </div>
              )}
              {item.objectType && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Тип объекта</div>
                  <div className={styles.ColValue}>{parseErknmValue(item.objectType.value)?.title}</div>
                </div>
              )}
              {item.objectKind && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Вид объекта</div>
                  <div className={styles.ColValue}>{parseErknmValue(item.objectKind.value)?.title}</div>
                </div>
              )}
              {item.objectSubKind && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Подвид объекта</div>
                  <div className={styles.ColValue}>{parseErknmValue(item.objectSubKind.value)?.title}</div>
                </div>
              )}
              {item.riskCategory && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Категория риска</div>
                  <div className={styles.ColValue}>{parseErknmValue(item.riskCategory.value)?.title}</div>
                </div>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
      {passportData.inspectionErknm?.documentRequestDate && (
        <KnmSubtitle title="Дата направления требования представить необходимые для рассмотрения в ходе документарной проверки документы">
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата</div>
            <div className={styles.ColValue}>
              {formatDateLocale({
                date: convertArrayToDate(passportData.inspectionErknm.documentRequestDate),
                dateFormat: DATE_FORMAT_LONG_FNS,
                parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
              })}
            </div>
          </div>
        </KnmSubtitle>
      )}
      {passportData.inspectionErknm?.documentResponseDate && (
        <KnmSubtitle
          title="Дата получения документов во исполнение требования представить необходимые для рассмотрения в ходе
  документарной проверки документы"
        >
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата</div>
            <div className={styles.ColValue}>
              {formatDateLocale({
                date: convertArrayToDate(passportData.inspectionErknm.documentResponseDate),
                dateFormat: DATE_FORMAT_LONG_FNS,
                parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
              })}
            </div>
          </div>
        </KnmSubtitle>
      )}
      {passportData.inspectionErknm?.inspectors && passportData.inspectionErknm.inspectors.length > 0 && (
        <KnmSubtitle title="Сведения о должностных лицах, участвующих в КНМ">
          {passportData.inspectionErknm.inspectors.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>Должностное лицо №{index + 1}</span>
              {item.fullName && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>ФИО</div>
                  <div className={styles.ColValue}>{item.fullName}</div>
                </div>
              )}
              {item.position && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Должность</div>
                  <div className={styles.ColValue}>{parseErknmValue(item.position.value)?.title}</div>
                </div>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
      {passportData.inspectionErknm?.experts && passportData.inspectionErknm.experts.length > 0 && (
        <KnmSubtitle
          title="Сведения об экспертах, экспертных организациях, специалистах,
  независимых органах инспекции, саморегулируемых организациях и иных лицах, привлекаемых для проведения КНМ"
        >
          {passportData.inspectionErknm.experts.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>Должностное лицо №{index + 1}</span>
              {item.title && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Наименование</div>
                  <div className={styles.ColValue}>{item.title}</div>
                </div>
              )}
              {item.type && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Тип</div>
                  <div className={styles.ColValue}>{item.type.name}</div>
                </div>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
      <KnmSubtitle title="Проведение мероприятия в форме совместного (межведомственного) контрольного (надзорного) мероприятия">
        <div className={styles.Row}>
          <div className={styles.ColText}>Проводится совместно</div>
          <div className={styles.ColValue}>
            {passportData.inspectionErknm?.isHasCollaboratingOrganization ? 'Да' : 'Нет'}
          </div>
        </div>
        {passportData.inspectionErknm?.collaboratingOrganizations &&
          passportData.inspectionErknm.collaboratingOrganizations.length > 0 && (
            <>
              <span className={styles.SectionTitle}>
                Наименование органов контроля , с которым КНМ проводится совместно
              </span>
              {passportData.inspectionErknm.collaboratingOrganizations.map((item, index) => (
                <div key={index}>
                  <span className={styles.SectionTitle}>Орган контроля №{index + 1}</span>
                  {item.organization && item.organization.value && (
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Наименование</div>
                      <div className={styles.ColValue}>{parseErknmValue(item.organization.value)?.fullName}</div>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
      </KnmSubtitle>
      {passportData.inspectionErknm?.interactionCategory && (
        <KnmSubtitle title="Сведения об использовании в рамках КНМ средств дистанционного взаимодействия с контролируемыми лицами">
          <div className={styles.Row}>
            <div className={styles.ColText}>Категория взаимодействия</div>
            <div className={styles.ColValue}>{passportData.inspectionErknm?.interactionCategory?.name}</div>
          </div>
        </KnmSubtitle>
      )}
      {!passportData.inspectionErknm?.interactionCategory && passportData.inspectionErknm?.isRemote !== null && (
        <KnmSubtitle title="Сведения об использовании в рамках КНМ средств дистанционного взаимодействия с контролируемыми лицами">
          <div className={styles.Row}>
            <div className={styles.ColText}>Отметка об использовании</div>
            <div className={styles.ColValue}>{passportData.inspectionErknm?.isRemote ? 'Да' : 'Нет'}</div>
          </div>
          {passportData.inspectionErknm?.noteRemote && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Сведения</div>
              <div className={styles.ColValue}>{passportData.inspectionErknm.noteRemote}</div>
            </div>
          )}
        </KnmSubtitle>
      )}
      {passportData.inspectionErknm?.events && passportData.inspectionErknm.events.length > 0 && (
        <KnmSubtitle title="Перечень действий, осуществляемых в рамках КНМ">
          {passportData.inspectionErknm.events.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>Действие №{index + 1}</span>
              {item.event && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Действие</div>
                  <div className={styles.ColValue}>{parseErknmValue(item.event.value)?.title}</div>
                </div>
              )}
              {item.interactionCategory && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Категория взаимодействия</div>
                  <div className={styles.ColValue}>{item.interactionCategory.name}</div>
                </div>
              )}
              {item.startDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата начала</div>
                  <div className={styles.ColValue}>
                    {formatDateLocale({
                      date: convertArrayToDate(item.startDate),
                      dateFormat: DATE_FORMAT_LONG_FNS,
                      parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
                    })}
                  </div>
                </div>
              )}
              {item.stopDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата окончания</div>
                  <div className={styles.ColValue}>
                    {formatDateLocale({
                      date: convertArrayToDate(item.stopDate),
                      dateFormat: DATE_FORMAT_LONG_FNS,
                      parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
    </div>
  );
});
